import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip, useToast } from "@humansignal/ui";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import { isCapacitor } from "../../utils/host";
import { downloadProjectForOffline, DownloadCancelled, getCheckpointSize } from "../../utils/offline-sync";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import "./ProjectTierBadge.prefix.css";

const MOBILE_DOWNLOAD_FLAG = (id) => `afri:mobile:project:${id}:downloaded`;

/** Fixed bottom-right progress card. Renders while
 *  ``downloadProjectForOffline`` is emitting progress events. Kept
 *  in this file (rather than a shared component) because it's tied
 *  1:1 to this button's ``progress`` state — no other caller needs
 *  it. Live phase + counts translate a "spinner for 5 minutes"
 *  into "Tasks 240 / 500" so a big project doesn't feel frozen.
 */
/* One continuous bar across the WHOLE pull. Phase-by-phase counters
   ("Tasks 166/166" then "Annotations 1/166" resetting) read as several
   runs; the weights below map every phase into a single 0-100 sweep,
   finishing once (owner request). */
const PHASE_SPAN = {
  metadata: [0, 5],
  tasks: [5, 30],
  annotations: [30, 96],
  done: [96, 100],
};
const overallPct = (progress) => {
  const span = PHASE_SPAN[progress.phase] || [0, 100];
  const total = typeof progress.total === "number" && progress.total > 0 ? progress.total : null;
  const fetched = typeof progress.fetched === "number" ? progress.fetched : 0;
  const frac = total ? Math.min(1, fetched / total) : 0.4;
  return Math.round(span[0] + (span[1] - span[0]) * frac);
};
const DownloadProgress = ({ progress, project, onCancel }) => {
  if (!progress) return null;
  return createPortal(
    <DownloadProgressCard progress={progress} project={project} onCancel={onCancel} />,
    document.body,
  );
};

/* Portaled to <body>: ``position: fixed`` inside the app's
   overscroll-contained scroller mispositions on iOS WKWebView — the
   card floated above the footer with a dead gap and its lower half
   clipped. Same fix as AppFooter. */
const DownloadProgressCard = ({ progress, project, onCancel }) => {
  const pct = progress.phase === "done" ? 100 : overallPct(progress);
  const total = typeof progress.total === "number" ? progress.total : null;
  const fetched = typeof progress.fetched === "number" ? progress.fetched : 0;
  const name = project?.title || `Project ${project?.id ?? ""}`;
  const b = cn("mobile-download-progress");
  return (
    <div className={b.toClassName()} role="status" aria-live="polite">
      <div className={b.elem("head").toClassName()}>
        <span className={b.elem("icon").toClassName()} aria-hidden="true">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 4v11" />
            <path d="M7 11l5 5 5-5" />
            <path d="M4 20h16" />
          </svg>
        </span>
        <span className={b.elem("text").toClassName()}>
          <span className={b.elem("title").toClassName()}>Downloading for offline</span>
          <span className={b.elem("name").toClassName()}>{name}</span>
        </span>
        <span className={b.elem("pct").toClassName()}>{pct}%</span>
      </div>
      <div className={b.elem("bar").toClassName()}>
        <div className={b.elem("bar-fill").toClassName()} style={{ width: `${Math.max(pct, 3)}%` }} aria-hidden />
      </div>
      <div className={b.elem("foot").toClassName()}>
        <span className={b.elem("counts").toClassName()}>
          {total != null && pct < 100 ? `${fetched} of ${total} items` : "Finishing up"}
        </span>
        {onCancel && (
          <button
            type="button"
            className={b.elem("cancel").toClassName()}
            onClick={onCancel}
            aria-label="Cancel download"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Per-project sync-tier indicator for the desktop app.
 *
 * Renders as an icon-only button that slots alongside the other
 * action-btns on a project card (settings, edit, offline-download)
 * — same 26×26 affordance, no extra horizontal real estate. The
 * tooltip carries the full explanation; the icon + colour signal
 * the tier at a glance.
 *
 *   • full           → green check-on-cloud (downloaded for offline)
 *   • available      → blue cloud-down arrow (one-click to download)
 *   • online_only    → muted cloud-slash (admin disallowed offline)
 *   • metadata_blocked → muted lock (owner blocked any local mirror)
 *
 * Hidden on the plain web build — afri_sync isn't loaded there.
 */

const isDesktopApp = () => {
  if (typeof window === "undefined") return false;
  const { hostname, port } = window.location;
  return (hostname === "127.0.0.1" || hostname === "localhost") && port === "17080";
};

const getCsrfToken = () => {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

// 14×14 inline SVGs to match action-btn icon sizing (Projects.prefix.css
// `__action-btn svg`). Kept inline so the badge is self-contained and
// doesn't depend on the @humansignal/icons set, which has no
// cloud-status glyphs at the time of writing.
const IconCloudCheck = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17.5 19a4.5 4.5 0 1 0-1.5-8.74A6 6 0 0 0 4 12.5 4 4 0 0 0 6.5 19h11Z" />
    <polyline points="9 13 11 15 15 11" />
  </svg>
);

const IconCloudDown = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17.5 19a4.5 4.5 0 1 0-1.5-8.74A6 6 0 0 0 4 12.5 4 4 0 0 0 6.5 19" />
    <path d="M12 12v8" />
    <polyline points="9 17 12 20 15 17" />
  </svg>
);

const IconCloudSlash = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17.5 19a4.5 4.5 0 1 0-1.5-8.74A6 6 0 0 0 4 12.5 4 4 0 0 0 6.5 19h11Z" />
    <line x1="4" y1="4" x2="20" y2="20" />
  </svg>
);

const IconLock = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const IconSpinner = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden>
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="42 14"
      opacity="0.85"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="0.9s"
        repeatCount="indefinite"
      />
    </circle>
  </svg>
);

export const ProjectTierBadge = ({ project, onChange }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Live phase + count for the mobile download progress bar. ``null``
  // when idle. Populated from ``downloadProjectForOffline``'s
  // ``onProgress`` callback so a long pull shows real motion instead
  // of a frozen busy state — 500 tasks × ~1s each is a lot to stare
  // at without a progress signal.
  const [progress, setProgress] = useState(null);
  // AbortController for the in-flight download. Held in a ref (not
  // state) so ``toggle`` reads the SAME controller instance as the
  // cancel button — a re-render between "start download" and "press
  // cancel" would otherwise leave the cancel button pointing at a
  // stale copy.
  const abortRef = useRef(null);

  // Mobile (Capacitor) gets the same affordance as desktop but
  // backed by the JS-side pull (``offline-sync.ts``) instead of
  // desktop's afri_sync POST. Plain web (browser) still hides the
  // badge since the offline pull is meaningless there.
  const onCapacitor = isCapacitor();
  if (!isDesktopApp() && !onCapacitor) return null;
  if (!project) return null;

  // On mobile, ``project.id`` IS the cloud id (Capacitor talks
  // direct to cloud, no local id namespace). Same project payload
  // on desktop carries an explicit ``cloud_id``.
  const cloudId = onCapacitor ? project.id : project.cloud_id;
  // Mobile uses the per-project ``allow_on_mobile`` flag set by
  // the project owner; desktop uses ``allow_offline_for_annotators``.
  const allowOffline = onCapacitor ? project.allow_on_mobile === true : project.allow_offline_for_annotators !== false;
  const allowMetadata = onCapacitor ? true : project.allow_metadata_mirror !== false;
  // Mobile state lives in localStorage (no Project model field —
  // pulling is a per-device action, not a per-project preference).
  const enabled = onCapacitor
    ? typeof window !== "undefined" && !!window.localStorage.getItem(MOBILE_DOWNLOAD_FLAG(cloudId))
    : !!project.offline_pref_enabled;
  const tier = onCapacitor
    ? !allowOffline
      ? "online_only"
      : enabled
        ? "full"
        : "available"
    : (project.local_sync_tier ?? "full");

  // Block toggling when the cloud is unreachable — Download for
  // offline NEEDS to fetch tasks/annotations/media from the cloud,
  // and a click while offline would just throw a network error
  // halfway through the pull. Better to disable the affordance.
  // ``useOnlineStatus`` is the same probe driving the menubar
  // OfflinePill.
  const cloudUp = useOnlineStatus();
  const togglable = allowOffline && allowMetadata && !!cloudId && cloudUp;

  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const toggle = useCallback(
    async (e) => {
      stop(e);
      if (!togglable || busy) return;
      setBusy(true);
      try {
        // Capacitor: JS-side pull. Hits the same cloud endpoints
        // desktop's afri_sync.run_pull walks (project metadata,
        // /api/projects/<id>/tasks/, per-task annotations + drafts)
        // and stashes everything in IndexedDB. The localStorage flag
        // tracks the per-device "downloaded" state so the badge
        // re-renders into the "full" tier on next mount.
        if (onCapacitor) {
          // Wire the progress callback so the modal below can render
          // "Tasks 42 / 500, annotations …" instead of a spinner. Only
          // emit every ~10 tasks upstream (offline-sync throttles) so
          // React isn't churning at fetch rate.
          setProgress({ phase: "metadata", fetched: 0, total: null });
          // Fresh controller per download attempt so a previously-
          // cancelled controller can't leak "aborted" state into the
          // next run. ``downloadProjectForOffline`` checks the signal
          // at every fetch boundary, so a cancel unwinds within ~1
          // request worth of latency.
          const ac = new AbortController();
          abortRef.current = ac;
          const stats = await downloadProjectForOffline(cloudId, (p) => setProgress(p), ac.signal);
          if (!enabled) {
            window.localStorage.setItem(MOBILE_DOWNLOAD_FLAG(cloudId), "1");
          }
          // P2 — enforce the media cap right after the pull, when the
          // store is at its largest. Lazy import keeps the eviction
          // module out of the badge's initial chunk.
          import("../../utils/offline-storage")
            .then((m) => m.evictMediaOverCap())
            .catch(() => {
              /* best-effort */
            });
          // Include annotations + drafts in the completion toast so the
          // annotator gets the FULL "what's on my device" signal, not
          // just the task count. Matters when a project has heavy prior
          // annotations (multi-annotator review) that they can now
          // reference offline.
          //
          // When ``resumedFrom > 0`` the toast leads with the resume
          // signal so the annotator understands why the pull finished
          // faster than they expected — "Resumed from N tasks" makes
          // the checkpoint feature legible instead of magical.
          const parts = [`${stats.tasks} task${stats.tasks === 1 ? "" : "s"}`];
          if (stats.annotations) parts.push(`${stats.annotations} annotation${stats.annotations === 1 ? "" : "s"}`);
          if (stats.drafts) parts.push(`${stats.drafts} draft${stats.drafts === 1 ? "" : "s"}`);
          const prefix =
            stats.resumedFrom > 0 ? `Resumed from ${stats.resumedFrom}: ${parts.join(", ")}` : parts.join(", ");
          toast.show({
            message: enabled ? `Refreshed offline cache. ${prefix}` : `Downloaded for offline use. ${prefix}`,
          });
          if (onChange) onChange();
          return;
        }

        // Desktop: existing afri_sync POST → run_pull on embedded Django.
        const r = await fetch("/api/afri/sync/set_offline_pref/", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfToken(),
          },
          body: JSON.stringify({
            cloud_project_id: cloudId,
            enabled: !enabled,
          }),
        });
        const text = await r.text();
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          /* ignore */
        }
        if (data.needs_confirm) {
          toast.show({
            type: "info",
            message: data.message || t("components.projecttierbadge.needs_confirm"),
          });
          return;
        }
        if (!r.ok) {
          toast.show({
            type: "error",
            message: data.error || `Failed (${r.status})`,
          });
          return;
        }
        toast.show({
          message: !enabled
            ? t("components.projecttierbadge.now_offline")
            : t("components.projecttierbadge.no_longer_offline"),
        });
        if (onChange) onChange();
      } catch (err) {
        // User-initiated cancel unwinds through DownloadCancelled — show
        // a neutral info toast summarising what DID make it into IDB
        // (a partial download is useful; the annotator can still work
        // offline on those tasks). Everything else is a real error.
        if (err instanceof DownloadCancelled) {
          toast.show({
            type: "info",
            message: "Download cancelled, the partial cache stays available.",
          });
        } else {
          toast.show({ type: "error", message: String(err?.message || err) });
        }
      } finally {
        setBusy(false);
        // Clear the progress card regardless of outcome so a failed pull
        // doesn't leave a phantom "Fetching tasks…" bar on screen.
        setProgress(null);
        abortRef.current = null;
      }
    },
    [togglable, busy, cloudId, enabled, onChange, toast, t, onCapacitor],
  );

  // Two layers:
  //   tooltip — short action-oriented label, matches the row's other
  //   action-btn tooltips ("Label tasks", "Project settings"). Visual.
  //   ariaLabel — longer descriptive sentence for screen readers /
  //   keyboard users who can't see the icon's colour as a signal.
  let Icon;
  let variant;
  let tooltip;
  let ariaLabel;
  let clickable;
  if (!allowMetadata) {
    Icon = IconLock;
    variant = "muted";
    tooltip = t("components.projecttierbadge.tip_cloud_only");
    ariaLabel = t("components.projecttierbadge.tooltip_metadata_blocked");
    clickable = false;
  } else if (tier === "full") {
    Icon = IconCloudCheck;
    variant = "ok";
    tooltip = t("components.projecttierbadge.tip_downloaded");
    ariaLabel = t("components.projecttierbadge.tooltip_offline_active");
    clickable = togglable;
  } else if (allowOffline) {
    Icon = IconCloudDown;
    variant = "info";
    tooltip = t("components.projecttierbadge.tip_download_for_offline");
    ariaLabel = t("components.projecttierbadge.tooltip_available_offline");
    clickable = togglable;
  } else {
    Icon = IconCloudSlash;
    variant = "muted";
    tooltip = t("components.projecttierbadge.tip_online_only");
    ariaLabel = t("components.projecttierbadge.tooltip_offline_disallowed");
    clickable = false;
  }

  const cardBlock = cn("project-card");
  const tierBlock = cn("project-tier-icon");
  const className = `${cardBlock.elem("action-btn").toClassName()} ${tierBlock.mod({ [variant]: true }).toClassName()}${
    clickable ? "" : ` ${tierBlock.mod({ static: true }).toClassName()}`
  }`;

  // Mirror the exact button shape used by the row's other action-btns
  // (Label tasks, Project settings) so Tooltip resolves children
  // consistently. ``disabled`` is omitted when not busy because
  // Tooltip's ``Children.only`` path branches on truthy ``disabled`` —
  // including a literal ``disabled={false}`` was suppressing the
  // tooltip in the desktop webview at smoke-test time.
  // Tooltip wrapping + a native ``title`` attribute as a fallback.
  // The fallback covers the desktop-webview case where the React
  // tooltip portal flickered out under a stacking context above it,
  // and also gives keyboard users a hover-equivalent on focus.
  if (clickable) {
    return (
      <>
        <Tooltip title={tooltip}>
          <button
            type="button"
            title={tooltip}
            className={className}
            onClick={toggle}
            aria-label={ariaLabel}
            {...(busy ? { disabled: true, "aria-busy": true } : {})}
          >
            {busy ? <IconSpinner /> : <Icon />}
          </button>
        </Tooltip>
        <DownloadProgress progress={progress} project={project} onCancel={() => abortRef.current?.abort()} />
      </>
    );
  }
  return (
    <Tooltip title={tooltip}>
      <span title={tooltip} className={className} aria-label={ariaLabel} role="img">
        <Icon />
      </span>
    </Tooltip>
  );
};
